import React, {ReactNode} from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Collapse,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ClearAll as ClearIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

export interface BaseFilters {
  search: string;
  [key: string]: any;
}

interface ListFiltersProps<T extends BaseFilters> {
  filters: T;
  onFiltersChange: (filters: T) => void;
  onClearFilters: () => void;
  expanded: boolean;
  onToggleFilters: () => void;
  searchPlaceholder?: string;
  children?: ReactNode;
  customActiveFilters?: ReactNode;
  startCollapsed?: boolean; // New prop to control if everything should be hidden initially
}

/**
 * Reusable filter component that provides search functionality and custom filter controls
 * Based on the existing SessionFilters component but made generic for reuse
 */
function ListFilters<T extends BaseFilters>({
  filters,
  onFiltersChange,
  onClearFilters,
  expanded,
  onToggleFilters,
  searchPlaceholder = "Search...",
  children,
  customActiveFilters,
  startCollapsed = false,
}: ListFiltersProps<T>) {
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      search: value,
    });
  };

  // Check if there are any active filters (excluding empty search)
  const hasActiveFilters = React.useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "search") return value.trim() !== "";
      if (typeof value === "string") return value !== "";
      if (typeof value === "number") return value !== 0;
      if (Array.isArray(value)) return value.length > 0;
      return false;
    });
  }, [filters]);

  // In startCollapsed mode, show only a toggle button when collapsed
  if (startCollapsed && !expanded) {
    return (
      <Box sx={{mb: 2, display: "flex", justifyContent: "flex-start"}}>
        <button
          onClick={onToggleFilters}
          className="btn btn-secondary btn-small"
          style={{borderRadius: "20px"}}
        >
          <FilterIcon sx={{fontSize: 16, marginRight: "4px"}} />
          Show Filters
        </button>

        {/* Active Filters Summary - Show even when collapsed if there are active filters */}
        {hasActiveFilters && (
          <Box
            sx={{
              ml: 2,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Active:
            </Typography>

            {/* Search filter chip */}
            {filters.search.trim() && (
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: "var(--sub)",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                }}
              >
                "{filters.search}"
              </Typography>
            )}

            {/* Custom active filters */}
            {customActiveFilters}
          </Box>
        )}
      </Box>
    );
  }

  // Normal mode or expanded state in startCollapsed mode
  return (
    <Paper sx={{mb: 2}}>
      {/* Search Bar - Always Visible in normal mode, or when expanded in startCollapsed mode */}
      <Box sx={{p: 2}}>
        <Box sx={{display: "flex", gap: 2, alignItems: "center"}}>
          <TextField
            fullWidth
            variant="outlined"
            id="list-filters-search-input"
            name="search"
            label="Search"
            placeholder={searchPlaceholder}
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              "aria-label": searchPlaceholder || "Search",
            }}
            sx={{flexGrow: 1}}
          />

          {/* Advanced Filters Toggle - Show hide/show text based on mode */}
          {(children || startCollapsed) && (
            <button
              onClick={onToggleFilters}
              className={`btn ${expanded ? "btn-primary" : "btn-secondary"} btn-small`}
              style={{borderRadius: "20px"}}
            >
              <FilterIcon sx={{fontSize: 16, marginRight: "4px"}} />
              {startCollapsed ? "Hide Filters" : "Filters"}
            </button>
          )}

          {/* Clear All Button */}
          <button
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
            className="btn btn-secondary btn-small"
            style={{borderRadius: "20px"}}
          >
            <ClearIcon sx={{fontSize: 16, marginRight: "4px"}} />
            Clear
          </button>
        </Box>
      </Box>

      {/* Advanced Filters - Collapsible (only show if there are children) */}
      {children && (
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{p: 2}}>{children}</Box>
        </Collapse>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <>
          <Divider />
          <Box sx={{p: 2, pt: 1}}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Active filters:
              </Typography>

              {/* Search filter chip */}
              {filters.search.trim() && (
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: "var(--sub)",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Search: "{filters.search}"
                </Typography>
              )}

              {/* Custom active filters */}
              {customActiveFilters}
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}

export default ListFilters;
